import { PropsWithChildren, ReactNode, useMemo, useRef, useState } from 'react';
import { CategoryInterface } from '../../../services/exports/Interfaces';
import useTheme from '../../../hooks/ui/useTheme';
import { useIntersection } from '../../../hooks/utility/useIntersection';
import useMenu from '../../../hooks/menu/useMenu';
import { debounce } from 'lodash';
import classnames from 'classnames';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import useScreenType from '../../../hooks/utility/useScreenType';

export interface GenericCategoryItemProps extends PropsWithChildren {
  category: CategoryInterface;
  index: number;
  isActive: boolean;
  loading?: boolean;
  onChangeVisibleIndex: (index: number) => void;
  customTitle?: string | ReactNode;
}

export default function GenericCategoryItem(props: GenericCategoryItemProps) {
  const { category, index, onChangeVisibleIndex } = props;
  const ref = useRef();
  const { isMobileApp } = useMobileApp();
  const [visible, setVisible] = useState<boolean>(false);

  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const onChangeVisibility = (visible: boolean) => {
    setVisible(visible);
    visible && onChangeVisibleIndexDebounce();
  };

  const onChangeVisibleIndexDebounce = debounce(() => {
    if (visibleRef.current) {
      onChangeVisibleIndex(index);
    }
  }, 0);

  useIntersection(
    ref,
    {
      rootMargin: '0px',
      threshold: 0,
    },
    false,
    onChangeVisibility,
  );

  const categoryTitleStyle = classnames('font-semibold', {
    'font-semibold': isMobileApp,
  });

  return (
    <div ref={ref} key={category?.id?.toString()}>
      <div className={classnames('lg:mb-lg mb-md space-y-xs', { 'space-y-[1px] px-md': isMobileApp })}>
        <div className={classnames(categoryTitleContainer)}>
          <div className="flex flex-row items-center justify-start gap-x-[8px] w-full">
            <div>
              <h5 className={categoryTitleStyle}>{props.customTitle ?? category?.name}</h5>
            </div>
          </div>
        </div>
        {category?.description && (
          <p
            className={classnames('text-muted-foreground text-sm', {
              'text-xs': isMobileApp,
            })}
          >
            {category?.description}
          </p>
        )}
      </div>
      {props.children}
    </div>
  );
}

GenericCategoryItem.Products = ({ category, children }: GenericCategoryItemProps) => {
  const { isMobileApp } = useMobileApp();
  const theme = useTheme();
  const { productHeight } = useMenu();
  const { isMobile } = useScreenType();
  const isTwoColumn = !isMobile;
  const productListSize = useMemo(() => {
    const productsGap = isTwoColumn ? 16 : 0;

    const productsPerColumn = Math.ceil(category?.products?.length / (isTwoColumn ? 2 : 1));

    return productsPerColumn * productHeight + (productsPerColumn - 1) * productsGap;
  }, [category?.products?.length, isTwoColumn, productHeight]);

  return (
    <div className={classnames({ 'px-md bg-white-100': isMobileApp })}>
      <div
        className={classnames(menuContainer, {
          'relative [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-brand-border':
            isMobileApp || !isTwoColumn,
        })}
        style={{
          gap: isTwoColumn ? theme.spacing.md : 0,
          height: productListSize,
        }}
      >
        {children}
      </div>
    </div>
  );
};

const menuContainer = 'grid md:grid-cols-2 grid-cols-1 mt-sm';

const categoryTitleContainer = 'flex flex-row items-center';
